import { useState } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { WorkbookEditor } from './useWorkbookEditor';
import { WorkbookSamplingDialog } from './WorkbookSamplingDialog';
import { useRunModeText, useRunModes } from './workbookRunModes';

/**
 * Beside Run workbook: Run on sample (the whole Workbook over its Sampling,
 * offered only when it can start) and the Sampling it samples by.
 */
export function WorkbookSampleRunMenu({
  editor,
  disabled,
  running,
  canUpdate,
  canExecute,
  act,
}: {
  editor: WorkbookEditor;
  disabled: boolean;
  /** A Run is under way, so another can't start; Sampling stays editable. */
  running: boolean;
  canUpdate: boolean;
  canExecute: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
}) {
  const gt = useGT();
  const [samplingOpen, setSamplingOpen] = useState(false);
  const sample = useRunModes(editor, null).sample;
  const { labels, blockerText } = useRunModeText(editor.definition);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            // The caret half of the toolbar's Run split button.
            className="rounded-l-none border-l border-l-primary-foreground/20 px-1.5"
            disabled={disabled}
            aria-label={gt('More ways to run', {
              context: 'workbook run options menu',
            })}
          >
            <CaretDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem
            disabled={running || !canExecute || !sample.allowed}
            onSelect={() => void act(() => editor.run('sample'))}
            className="flex-col items-start gap-0.5"
          >
            <span>{labels.sample}</span>
            <span className="text-xs text-muted-foreground">
              {sample.blocker
                ? blockerText(sample.blocker)
                : gt(
                    'Recalculate every sheet over a small sample of the data, in seconds. Its results are never downloaded.',
                    { context: 'workbook run mode description' },
                  )}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setSamplingOpen(true)}>
            <T context="workbook sampling menu item">Sampling…</T>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <WorkbookSamplingDialog
        editor={editor}
        open={samplingOpen}
        onOpenChange={setSamplingOpen}
        canUpdate={canUpdate}
      />
    </>
  );
}
